import { type ReactElement, useMemo, useState } from 'react';
import { Container, Stack, Tab, Tabs, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
import type {
  MarkdownRemarkFrontmatterCopy,
  MarkdownRemarkFrontmatterGlobalPayoutData,
} from '../../../generated/types';
import { GlobalPayoutMap } from '../../components/global-payout/GlobalPayoutMap';
import { GlobalPayoutSelect } from '../../components/global-payout/GlobalPayoutSelect';
import { useBreakpoints } from '../../components/hooks/useBreakpoints';

type TpActiveTab = 'cardAcceptance' | 'payout';

type PpMapSection = {
  copy: MarkdownRemarkFrontmatterCopy;
  data: MarkdownRemarkFrontmatterGlobalPayoutData[];
  subtitle: string | null | undefined;
  title: string | null | undefined;
};

const StyledContainer = styled(Container)(({ theme }) => ({
  marginTop: theme.spacing(12),
  maxWidth: theme.breakpoints.values.lg,
  paddingBottom: theme.spacing(12),
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(0, 3, 6),
  },
})) as typeof Container;

const StyledTab = styled(Tab)(({ theme }) => ({
  ...theme.typography.paragraphBodySBold,
  color: theme.palette.websiteBrand.greyscale.grey,
  '&.Mui-selected': {
    color: theme.palette.websiteBrand.greyscale.black,
  },
}));

function getCurrentData(
  data: MarkdownRemarkFrontmatterGlobalPayoutData[],
  activeTab: TpActiveTab,
): MarkdownRemarkFrontmatterGlobalPayoutData[] {
  if (activeTab === 'cardAcceptance') {
    return data.map(({ payoutChannels, ...rest }) => rest).filter((item) => item.cardAcceptance);
  }
  return data;
}

export function MapSection({ copy, data, subtitle, title }: PpMapSection): ReactElement {
  const [activeTab, setActiveTab] = useState<TpActiveTab>('payout');
  const { isMedium } = useBreakpoints();

  const memoizedData = useMemo(() => {
    const globalPayoutData = getCurrentData(data, activeTab);
    const availableCountries = globalPayoutData.map((item) => item.country).sort();
    return { availableCountries, globalPayoutData };
  }, [activeTab, data]);

  return (
    <StyledContainer disableGutters fixed component="section">
      <Stack alignItems="center" spacing={4}>
        <Stack maxWidth="md" spacing={2.5} textAlign="center">
          <Typography variant="headingL">{title}</Typography>
          <Typography color="text.dark" variant="paragraphBodyM">
            {subtitle}
          </Typography>
        </Stack>
        <Tabs
          value={activeTab}
          onChange={(_, newValue: TpActiveTab): void => setActiveTab(newValue)}
        >
          <StyledTab label={copy.tab1} value="payout" />
          <StyledTab label={copy.tab2} value="cardAcceptance" />
        </Tabs>
        {isMedium ? (
          <GlobalPayoutSelect
            availableCountries={memoizedData.availableCountries}
            copy={copy}
            data={memoizedData.globalPayoutData}
          />
        ) : (
          <GlobalPayoutMap
            availableCountries={memoizedData.availableCountries}
            copy={copy}
            data={memoizedData.globalPayoutData}
          />
        )}
      </Stack>
    </StyledContainer>
  );
}
